export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

/**
 * Thrown by parseResponse when the server returns a non-2xx HTTP status.
 * Exposes the numeric `status` code so callers can handle specific statuses
 * (e.g. 404 - return null, 401 - redirect to login) without string-parsing.
 *
 * @example
 * try {
 *   return await client.get('myFunction');
 * } catch (error) {
 *   if (error instanceof HttpError && error.status === 404) return null;
 *   throw error;
 * }
 */
export class HttpError extends Error {
  readonly status: number;
  readonly body: string;

  constructor(status: number, body: string) {
    super(`HTTP error! status: ${status}, message: ${body}`);
    this.name = 'HttpError';
    this.status = status;
    this.body = body;
  }
}
export type AuthStrategy = 'bearer' | 'appcheck';

export interface ProjectConfig {
  /**
   * Returns the full URL for a request given the service/function name, environment, and optional sub-path.
   *
   * Called lazily on each request so env vars are read at call time, not import time.
   *
   * @param serviceName - The Cloud Function name or Cloud Run service name.
   * @param isProd - Whether the app is running in production. Configs use this to switch URLs or suffixes.
   * @param path - Optional sub-path appended after the base URL (Cloud Run endpoints like '/counts').
   */
  getUrl(serviceName: string, isProd: boolean, path?: string): string;
}

export interface RequestOptions {
  /** Additional headers merged with the default Authorization and Content-Type headers. */
  headers?: Record<string, string>;
  /** Overrides the default 'application/json' content type. */
  contentType?: string;
  /** When true, forces a token refresh before the request (passes true to getIdToken). */
  forceTokenRefresh?: boolean;
  /**
   * Optional sub-path appended to the service URL. Used for Cloud Run services that expose multiple
   * endpoints under a single service (e.g. 'counts', 'filter').
   * Leading and trailing slashes are normalized automatically.
   */
  path?: string;
}
